import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

import { useUser } from '../../../context/UserContext';
import { useNotification } from '../../../context/NotificationContext';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import { useOrganizationLocations } from '../../../hooks/useOrganizationData';
import { useStoreWebsite } from '../hooks/useStoreWebsite';
import { storeWebsiteApi } from '../services/storeWebsiteApi';

import FormButton from '../../../components/common/stories/FormButton';
import '../../settings/Settings.css';
import '../StoreWebsiteSettings.css';

const WebsitePageWrapper = ({ children, title, subtitle, userOrganizationId }) => {
  const { isAdmin, isLoading: isUserLoading } = useUser();
  const navigate = useNavigate();
  const activeOrganizationId = useSelectedOrganization(userOrganizationId);
  const notification = useNotification();

  const { data: locations = [], isLoading: isLoadingLocations } =
    useOrganizationLocations(activeOrganizationId);
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  const {
    website,
    pages,
    isLoading: isLoadingWebsite,
    isSaving,
    createWebsite,
    updateWebsite,
    togglePublish,
    getPublicUrl,
    platformDomain,
  } = useStoreWebsite(selectedLocationId);

  // Redirect non-admins
  useEffect(() => {
    if (!isUserLoading && !isAdmin()) {
      notification.error('You do not have permission to access this page');
      navigate('/dashboard');
    }
  }, [isAdmin, isUserLoading, navigate, notification]);

  // Pick a default location once they load
  useEffect(() => {
    if (locations.length > 0) {
      setSelectedLocationId(locations[0]._id);
    }
  }, [locations]);

  const selectedLocation = locations.find((l) => l._id === selectedLocationId);

  const handleCreateWebsite = async () => {
    if (!selectedLocation) return;

    const subdomainSlug = selectedLocation.city
      ? storeWebsiteApi.generateSlug(selectedLocation.city)
      : 'main';
    const orgSlug = storeWebsiteApi.generateSlug(selectedLocation.store_name || 'store');

    await createWebsite({
      organization_id: activeOrganizationId,
      subdomain_slug: subdomainSlug,
      org_slug: orgSlug,
      is_published: false,
      theme: {
        primary_color: '#ff8264',
        logo_url: '',
        banner_image_url: '',
      },
      seo: {
        title: selectedLocation.store_name || 'Store',
        description: `Weekly flyers and deals at ${selectedLocation.store_name}`,
        og_image_url: '',
      },
      social: {
        facebook: '',
        instagram: '',
        twitter: '',
      },
    });
  };

  const handleLocationChange = (e) => {
    setSelectedLocationId(e.target.value);
  };

  const publicUrl = getPublicUrl();

  if (isUserLoading || isLoadingLocations) {
    return (
      <div className="website-page">
        <div className="website-page-loading">
          <CircularProgress />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className="website-page">
        <div className="website-page-header">
          <div className="website-page-header-left">
            <div className="website-page-title-group">
              <h1 className="website-page-title">{title}</h1>
              {subtitle && <p className="website-page-subtitle">{subtitle}</p>}
            </div>
          </div>
        </div>
        <div className="website-page-content">
          <div className="website-card">
            <div className="website-empty-state">
              <h3>No Store Locations</h3>
              <p>You need to create at least one store location before you can set up a website.</p>
              <FormButton onClick={() => navigate('/settings/locations')}>
                Manage Locations
              </FormButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render function for child content - passes render props
  const renderContent = () => {
    if (isLoadingWebsite) {
      return (
        <>
          <div className="website-page-content">
            <div className="website-card">
              <div className="website-page-loading">
                <CircularProgress />
              </div>
            </div>
          </div>
        </>
      );
    }

    if (!website) {
      return (
        <>
          <div className="website-page-content">
            <div className="website-card">
              <div className="website-empty-state">
                <h3>Create a Website for {selectedLocation?.store_name}</h3>
                <p>
                  Set up a public website to showcase your weekly flyers and store information.
                  Customers will be able to view your current flyer, store hours, and contact details.
                </p>
                <FormButton onClick={handleCreateWebsite} disabled={isSaving}>
                  {isSaving ? 'Creating...' : 'Create Website'}
                </FormButton>
              </div>
            </div>
          </div>
        </>
      );
    }

    // Call children render prop and render content + actionBar
    const ChildContent = children;
    return (
      <ChildContent
        website={website}
        pages={pages}
        updateWebsite={updateWebsite}
        togglePublish={togglePublish}
        isSaving={isSaving}
        platformDomain={platformDomain}
        locationId={selectedLocationId}
        publicUrl={publicUrl}
      />
    );
  };

  return (
    <div className="website-page">
      {/* Page Header */}
      <div className="website-page-header">
        <div className="website-page-header-left">
          <div className="website-page-title-group">
            <h1 className="website-page-title">{title}</h1>
            {subtitle && <p className="website-page-subtitle">{subtitle}</p>}
          </div>
        </div>
        <div className="website-page-header-right">
          {website && (
            <>
              <button
                className={`website-status-badge ${website.is_published ? 'published' : 'unpublished'}`}
                onClick={togglePublish}
                disabled={isSaving}
                title={website.is_published ? 'Click to unpublish' : 'Click to publish'}
                style={{ cursor: 'pointer', border: 'none' }}
              >
                <span className="status-dot"></span>
                <span>{website.is_published ? 'Published' : 'Unpublished'}</span>
              </button>
              {publicUrl && (
                <button
                  className="website-view-btn"
                  onClick={() => window.open(publicUrl, '_blank')}
                >
                  <OpenInNewIcon style={{ fontSize: 16 }} />
                  <span>View Website</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Location Selector */}
      <div className="website-location-bar">
        <div className="website-location-selector">
          <label htmlFor="location-select">Select Location:</label>
          <select
            id="location-select"
            value={selectedLocationId || ''}
            onChange={handleLocationChange}
            className="website-location-dropdown"
          >
            {locations.map((location) => (
              <option key={location._id} value={location._id}>
                {location.store_name}
                {location.city && ` - ${location.city}`}
              </option>
            ))}
          </select>
        </div>

        {/* Website URL Display */}
        {publicUrl && website && (
          <div className="website-url-bar">
            <span className="website-url-label">Your website URL:</span>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="website-url-link"
            >
              {publicUrl}
              <OpenInNewIcon style={{ fontSize: 12, marginLeft: 4 }} />
            </a>
          </div>
        )}
      </div>

      {/* Main Content and Action Bar */}
      {renderContent()}
    </div>
  );
};

export default WebsitePageWrapper;
